import React, { useCallback, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import '../styling/GameSection.css';
import '../styling/ScrollBar.css';
import headshot from '../images/headshot.jpg';

const roleIcons = {
    "Programming": "\u{1F4BB}",
    "2D Art": "\u{1F3A8}",
    "3D Modeling": "\u{1F9CA}",
    "Design": "\u{2696}",
    "Sound Design": "\u{1F50A}",
    "Music": "\u{1F3B5}",
    "Writing": "\u{270F}",
    "Game Feel": "\u{1F3AE}",
    "Puzzle Design": "\u{1F9E9}",
};

let matrixActive = false;
let matrixDepth = 0;

const generateTearEdge = (points, side, baseX, amplitude) => {
    const path = [];
    let prev = 0;
    for (let i = 0; i <= points; i++) {
        const t = i / points;
        const y = t * 100;
        // Random walk with some continuity
        const jitter = (Math.random() - 0.5) * 1.8;
        prev = prev * 0.3 + jitter * 0.7;
        // Occasional sharp spike
        const spike = Math.random() < 0.12 ? (Math.random() - 0.5) * 2.5 : 0;
        const x = baseX + (prev + spike) * amplitude;
        path.push(`${x}% ${y}%`);
    }
    return path;
};

let matrixOverlay = null;
let matrixAnimFrame = null;
let matrixRainFrame = null;
let matrixTargetDepth = 0;
let matrixCurrentDepth = 0;
let matrixClosing = false;
let matrixCloseTimer = null;

const triggerMatrix = () => {
    matrixDepth = Math.min(matrixDepth + 0.1, 1);
    matrixTargetDepth = matrixDepth;
    matrixClosing = false;

    // Reset close timer
    if (matrixCloseTimer) clearTimeout(matrixCloseTimer);
    matrixCloseTimer = setTimeout(() => {
        matrixClosing = true;
    }, 3000);

    if (matrixActive) return;
    matrixActive = true;

    const overlay = document.createElement("div");
    overlay.className = "matrix-overlay";
    document.body.appendChild(overlay);
    matrixOverlay = overlay;

    const leftCanvas = document.createElement("canvas");
    const rightCanvas = document.createElement("canvas");
    leftCanvas.className = "matrix-canvas matrix-left";
    rightCanvas.className = "matrix-canvas matrix-right";
    overlay.appendChild(leftCanvas);
    overlay.appendChild(rightCanvas);

    const w = window.innerWidth;
    const h = window.innerHeight;
    const tearWidth = w;

    leftCanvas.width = tearWidth;
    leftCanvas.height = h;
    rightCanvas.width = tearWidth;
    rightCanvas.height = h;

    const fontSize = 14;
    const colsL = Math.floor(tearWidth / fontSize);
    const colsR = Math.floor(tearWidth / fontSize);
    const dropsL = Array(colsL).fill(0).map(() => Math.random() * -50 | 0);
    const dropsR = Array(colsR).fill(0).map(() => Math.random() * -50 | 0);

    const drawRain = (ctx, cols, drops, canvasW, canvasH) => {
        ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
        ctx.fillRect(0, 0, canvasW, canvasH);
        ctx.font = `${fontSize}px monospace`;

        for (let i = 0; i < cols; i++) {
            const char = Math.random() > 0.5 ? "1" : "0";
            const x = i * fontSize;
            const y = drops[i] * fontSize;
            ctx.fillStyle = `rgba(0, ${150 + Math.random() * 105}, 0, ${0.7 + Math.random() * 0.3})`;
            ctx.fillText(char, x, y);
            if (y > canvasH && Math.random() > 0.975) drops[i] = 0;
            drops[i]++;
        }
    };

    [leftCanvas, rightCanvas].forEach(c => {
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, c.width, c.height);
    });

    const edgePoints = 60;

    const buildClip = (side, pts) => {
        if (side === "left") return `polygon(0% 0%, ${pts.join(", ")}, 0% 100%)`;
        return `polygon(100% 0%, ${pts.join(", ")}, 100% 100%)`;
    };

    const randL = Array.from({ length: edgePoints + 1 }, () => Math.random() * 6 - 3);
    const randR = Array.from({ length: edgePoints + 1 }, () => Math.random() * 6 - 3);

    // depth 1 = just a sliver (baseX near edge), depth 10 = nearly center
    const getEdge = (side, openness, time) => {
        // baseX slides from 95/5 (barely visible) to 50 (center) based on depth
        const depthFraction = matrixCurrentDepth;
        const baseX = side === "left" ? 5 + depthFraction * 45 : 95 - depthFraction * 45;
        const closedX = side === "left" ? 0 : 100;
        const rand = side === "left" ? randL : randR;
        const pts = [];
        for (let i = 0; i <= edgePoints; i++) {
            const y = (i / edgePoints) * 100;
            const amp = depthFraction * 8;
            // Massive slow wave
            const massive = Math.sin(i * 0.25 + time * 0.2) * depthFraction * 60;
            // Broad slow wave
            const broad = Math.sin(i * 0.8 + time * 0.4) * amp * 2.5;
            // Medium movement
            const n1 = Math.sin(i * 3.1 + time * 0.9) * amp;
            const n2 = Math.sin(i * 11.3 + time * 2.7 + 4.2) * amp * 0.6;
            // Fine noise
            const n3 = Math.sin(i * 29.7 + time * 4.1 + 1.8) * amp * 0.3;
            const spike = Math.sin(i * 17.3 + time * 3.5) > 0.5 ? Math.sin(time * 2.1 + i * 7.7) * amp * 1.2 : 0;
            const perPoint = rand[i] * Math.sin(time * 1.3 + i * 0.7) * amp * 0.5;
            const openX = baseX + (massive + broad + n1 + n2 + n3 + spike + perPoint);
            const x = closedX + (openX - closedX) * openness;
            pts.push(`${x}% ${y}%`);
        }
        return pts;
    };

    let startTime = performance.now();
    let closeProgress = 0;

    const animate = (now) => {
        const elapsed = now - startTime;
        const time = elapsed * 0.003;

        // Smoothly interpolate current depth toward target
        if (matrixClosing) {
            closeProgress += 0.02;
            if (closeProgress >= 1) {
                cancelAnimationFrame(matrixRainFrame);
                overlay.remove();
                matrixActive = false;
                matrixOverlay = null;
                matrixDepth = 0;
                matrixCurrentDepth = 0;
                matrixTargetDepth = 0;
                matrixClosing = false;
                return;
            }
        } else {
            matrixCurrentDepth += (matrixTargetDepth - matrixCurrentDepth) * 0.08;
            closeProgress = 0;
        }

        const openness = matrixClosing ? 1 - (closeProgress * closeProgress) : 1;

        leftCanvas.style.clipPath = buildClip("left", getEdge("left", openness, time));
        rightCanvas.style.clipPath = buildClip("right", getEdge("right", openness, time));

        matrixAnimFrame = requestAnimationFrame(animate);
    };

    const animateRain = () => {
        drawRain(leftCanvas.getContext("2d"), colsL, dropsL, tearWidth, h);
        drawRain(rightCanvas.getContext("2d"), colsR, dropsR, tearWidth, h);
        matrixRainFrame = requestAnimationFrame(animateRain);
    };

    matrixRainFrame = requestAnimationFrame(animateRain);
    matrixAnimFrame = requestAnimationFrame(animate);
};

const Carousel = ({ images, alt }) => {
    const [current, setCurrent] = useState(0);
    const timerRef = useRef(null);

    const goTo = useCallback((index) => {
        setCurrent((index + images.length) % images.length);
    }, [images.length]);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCurrent(c => (c + 1) % images.length);
        }, 4000);
        return () => clearInterval(timerRef.current);
    }, [images.length]);

    const resetTimer = useCallback(() => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setCurrent(c => (c + 1) % images.length);
        }, 4000);
    }, [images.length]);

    return (
        <div className="carousel">
            <div className="carouselTrack" style={{ transform: `translateX(-${current * 100}%)` }}>
                {images.map((src, i) => (
                    <img key={i} src={src} alt={`${alt} ${i + 1}`} className="carouselSlide" />
                ))}
            </div>
            <button className="carouselArrow carouselPrev" onClick={() => { goTo(current - 1); resetTimer(); }}>&#8249;</button>
            <button className="carouselArrow carouselNext" onClick={() => { goTo(current + 1); resetTimer(); }}>&#8250;</button>
            <div className="carouselDots">
                {images.map((_, i) => (
                    <button key={i} className={`carouselDot ${i === current ? "active" : ""}`} onClick={() => { goTo(i); resetTimer(); }} />
                ))}
            </div>
        </div>
    );
};

const GameFeelTag = ({ containerRef }) => {
    const tagRef = useRef(null);

    const spawnParticles = useCallback((pos) => {
        const count = 12;
        for (let i = 0; i < count; i++) {
            const particle = document.createElement("span");
            particle.className = "gf-particle";
            const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
            const distance = 60 + Math.random() * 80;
            particle.style.setProperty("--dx", `${Math.cos(angle) * distance}px`);
            particle.style.setProperty("--dy", `${Math.sin(angle) * distance}px`);
            particle.style.left = `${pos.x}px`;
            particle.style.top = `${pos.y}px`;
            document.body.appendChild(particle);
            setTimeout(() => particle.remove(), 600);
        }
    }, []);

    const spawnShockwave = useCallback((pos) => {
        const wave = document.createElement("span");
        wave.className = "gf-shockwave";
        wave.style.left = `${pos.x}px`;
        wave.style.top = `${pos.y}px`;
        document.body.appendChild(wave);
        setTimeout(() => wave.remove(), 500);
    }, []);

    const intensity = useRef(0);

    const handleClick = useCallback((e) => {
        intensity.current += 1;
        const power = intensity.current;
        // Restart tag shake animation
        const tag = tagRef.current;
        if (tag) {
            tag.classList.remove("gf-shake");
            void tag.offsetWidth;
            tag.classList.add("gf-shake");
        }

        const rect = tag?.getBoundingClientRect();
        const clickPos = { x: e.clientX, y: e.clientY };
        const centerPos = rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : clickPos;
        for (let burst = 0; burst < power; burst++) {
            setTimeout(() => spawnParticles(clickPos), burst * 30);
        }
        spawnShockwave(centerPos);

        // Screen shake with increasing intensity
        const container = containerRef.current;
        if (container) {
            container.style.setProperty("--shake-intensity", Math.min(power * 4, 30) + "px");
            container.classList.remove("screenshake");
            void container.offsetWidth; // force reflow to restart animation
            container.classList.add("screenshake");
            setTimeout(() => container.classList.remove("screenshake"), 400);
        }

        // Decay intensity over time
        setTimeout(() => {
            intensity.current = Math.max(0, intensity.current - 1);
        }, 800);
    }, [containerRef, spawnParticles, spawnShockwave]);

    return (
        <button
            ref={tagRef}
            className="tag gameFeelTag"
            onClick={handleClick}
        >
            <span className="tagIcon">{roleIcons["Game Feel"]}</span>
            Game Feel
        </button>
    );
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);
    return isMobile;
};

const TappableHeadshot = ({ containerRef }) => {
    const wrapperRef = useRef(null);
    const imgRef = useRef(null);
    const [loose, setLoose] = useState(false);
    const taps = useRef(0);
    const physics = useRef({ x: 0, y: 0, vx: 0, vy: 0, rot: 0, vr: 0 });
    const animRef = useRef(null);
    const sizeRef = useRef(280);

    const spawnParticles = useCallback((pos) => {
        const count = 12;
        for (let i = 0; i < count; i++) {
            const particle = document.createElement("span");
            particle.className = "gf-particle";
            const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
            const distance = 60 + Math.random() * 80;
            particle.style.setProperty("--dx", `${Math.cos(angle) * distance}px`);
            particle.style.setProperty("--dy", `${Math.sin(angle) * distance}px`);
            particle.style.left = `${pos.x}px`;
            particle.style.top = `${pos.y}px`;
            document.body.appendChild(particle);
            setTimeout(() => particle.remove(), 600);
        }
    }, []);

    const spawnShockwave = useCallback((pos) => {
        const wave = document.createElement("span");
        wave.className = "gf-shockwave";
        wave.style.left = `${pos.x}px`;
        wave.style.top = `${pos.y}px`;
        document.body.appendChild(wave);
        setTimeout(() => wave.remove(), 500);
    }, []);

    // Physics loop
    const startPhysics = useCallback((initialVx, initialVy) => {
        const hero = wrapperRef.current?.closest('.heroSection');
        if (!hero) return;

        const img = imgRef.current;
        if (!img) return;

        const heroRect = hero.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        const size = sizeRef.current;
        const p = physics.current;

        // Calculate position before reparenting
        p.x = imgRect.left - heroRect.left;
        p.y = imgRect.top - heroRect.top;
        p.vx = initialVx;
        p.vy = initialVy;
        p.rot = 0;

        // Set position immediately, then reparent
        img.style.left = `${p.x}px`;
        img.style.top = `${p.y}px`;
        img.style.transform = `rotate(0deg)`;
        hero.appendChild(img);

        const gravity = 0.4;
        const bounce = 0.7;
        const friction = 0.99;

        const step = () => {
            p.vy += gravity;
            p.vx *= friction;
            p.x += p.vx;
            p.y += p.vy;
            p.rot += p.vx * 0.5;
            p.vr = p.vx * 0.5;

            // Bounce off walls
            const maxX = heroRect.width - size;
            const maxY = heroRect.height - size;

            if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx) * bounce; }
            if (p.x > maxX) { p.x = maxX; p.vx = -Math.abs(p.vx) * bounce; }
            if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy) * bounce; }
            if (p.y > maxY) { p.y = maxY; p.vy = -Math.abs(p.vy) * bounce; }

            const img = imgRef.current;
            if (img) {
                img.style.left = `${p.x}px`;
                img.style.top = `${p.y}px`;
                img.style.transform = `rotate(${p.rot}deg)`;
            }

            animRef.current = requestAnimationFrame(step);
        };

        if (animRef.current) cancelAnimationFrame(animRef.current);
        animRef.current = requestAnimationFrame(step);
    }, []);

    const handleClick = useCallback((e) => {
        const img = imgRef.current;
        if (!img) return;

        const clickPos = { x: e.clientX, y: e.clientY };
        spawnParticles(clickPos);

        if (!loose) {
            taps.current += 1;

            const rect = img.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const direction = e.clientX < centerX ? -1 : 1;
            const angle = Math.min(taps.current * 8, 40) * direction;

            img.style.transition = 'none';
            img.style.transform = `rotate(${angle}deg)`;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    img.style.transition = 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)';
                    img.style.transform = 'rotate(0deg)';
                });
            });

            const centerPos = { x: centerX, y: rect.top + rect.height / 2 };
            spawnShockwave(centerPos);

            const container = containerRef.current;
            if (container) {
                container.style.setProperty("--shake-intensity", Math.min(taps.current * 4, 30) + "px");
                container.classList.remove("screenshake");
                void container.offsetWidth;
                container.classList.add("screenshake");
                setTimeout(() => container.classList.remove("screenshake"), 400);
            }

            if (taps.current >= 5) {
                setLoose(true);
                img.style.transition = 'none';
                img.style.transformOrigin = 'center center';
                const dx = e.clientX - (rect.left + rect.width / 2);
                const dy = e.clientY - (rect.top + rect.height / 2);
                const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                startPhysics((dx / dist) * -15, (dy / dist) * -15);
            }
        } else {
            // Already loose: apply force from click toward center
            const rect = img.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dx = e.clientX - cx;
            const dy = e.clientY - cy;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const force = 20;
            physics.current.vx += (dx / dist) * -force;
            physics.current.vy += (dy / dist) * -force;

            const centerPos = { x: cx, y: cy };
            spawnShockwave(centerPos);

            const container = containerRef.current;
            if (container) {
                container.style.setProperty("--shake-intensity", "12px");
                container.classList.remove("screenshake");
                void container.offsetWidth;
                container.classList.add("screenshake");
                setTimeout(() => container.classList.remove("screenshake"), 400);
            }
        }
    }, [loose, containerRef, spawnParticles, spawnShockwave, startPhysics]);

    useEffect(() => {
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, []);

    return (
        <div className="headshotWrapper" ref={wrapperRef}>
            <p className="headshotSecret">hello world</p>
            {loose ? (
                <img
                    ref={imgRef}
                    src={headshot}
                    alt="Nick van Gerwen"
                    className="heroHeadshotLoose"
                    onClick={handleClick}
                    draggable={false}
                />
            ) : (
                <img
                    ref={imgRef}
                    src={headshot}
                    alt="Nick van Gerwen"
                    className="heroHeadshot"
                    style={{ cursor: 'pointer' }}
                    onClick={handleClick}
                    draggable={false}
                />
            )}
        </div>
    );
};

const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
};

const Home = ({ games = [] }) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const sectionRefs = useRef([]);
    const containerRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const containerRect = container.getBoundingClientRect();
            const containerCenter = containerRect.top + containerRect.height / 2;
            let closest = -1;
            let closestDist = Infinity;

            sectionRefs.current.forEach((ref, index) => {
                if (!ref) return;
                const rect = ref.getBoundingClientRect();
                const sectionCenter = rect.top + rect.height / 2;
                const dist = Math.abs(sectionCenter - containerCenter);
                if (dist < closestDist) {
                    closestDist = dist;
                    closest = index;
                }
            });

            setActiveIndex(closest);
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => container.removeEventListener("scroll", handleScroll);
    }, [games]);

    return (
        <div className="snapContainer" ref={containerRef}>
            <div className="heroSection">
                <div className="heroContent">
                    <TappableHeadshot containerRef={containerRef} />
                    <div className="heroText">
                        <h1 className="heroName">Nick van Gerwen</h1>
                        <p className="heroTagline">Game Programmer & Designer</p>
                        <p className="heroBio">
                            Hi! I'm Nick, a game programmer and designer from the Netherlands.
                            I graduated from Fontys Hogeschool Eindhoven in Game Design & Technology
                            and have been working in the industry since. I love expressing my
                            creativity, sometimes through music, sometimes through digital art,
                            but mostly by making games.
                        </p>
                    </div>
                </div>
                <div className="heroScrollHint">
                    <p className="heroScroll">Scroll down to see my work</p>
                    <span className="heroChevron"></span>
                </div>
            </div>

            {games.map((game, index) => {
                const isActive = activeIndex === index;
                const screenshots = game.screenshots || [game.backgroundImage];
                const hasMultiple = screenshots.length > 1;

                return (
                    <div
                        className={`gameSection ${isActive ? "active" : ""}`}
                        key={game.id}
                        ref={(el) => (sectionRefs.current[index] = el)}
                        data-index={index}
                        style={{
                            backgroundColor: `#${game.bgColor}`,
                            backgroundImage: `url(${game.backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    >
                        <div className="gameLayout">
                            <div className="infoCard">
                                <div className="infoRow topRow">
                                    <img
                                        src={game.thumbnail}
                                        height={100}
                                        alt={`${game.title} thumbnail`}
                                        className="infoThumbnail"
                                    />
                                    <div className="infoTextColumn">
                                        <p className="gameTitle" style={{ color: `#${game.fgColor}` }}>
                                            {game.title}
                                        </p>
                                        <p className="gameDescription" style={{ color: `#${game.fgColor}` }}>
                                            {game.description}
                                        </p>
                                    </div>
                                </div>

                                <div className="rolesSection">
                                    <p className="rolesHeader">My contributions</p>
                                    <div className="tagsContainer">
                                        {game.tags?.map((tag, i) =>
                                            tag === "Game Feel" ? (
                                                <GameFeelTag key={i} containerRef={containerRef} />
                                            ) : tag === "Programming" ? (
                                                <button key={i} className="tag programmingTag" onClick={triggerMatrix}>
                                                    <span className="tagIcon">{roleIcons[tag]}</span>
                                                    {tag}
                                                </button>
                                            ) : (
                                                <p key={i} className="tag">
                                                    <span className="tagIcon">{roleIcons[tag] || ""}</span>
                                                    {tag}
                                                </p>
                                            )
                                        )}
                                        <a
                                            href={game.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tag playButton"
                                            style={{ backgroundColor: `#${game.linkColor}` }}
                                        >
                                            &#9654; itch.io
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="screenshotCard">
                                {hasMultiple ? (
                                    isMobile ? (
                                        <Carousel images={screenshots} alt={game.title} />
                                    ) : (
                                        <Slider {...sliderSettings}>
                                            {screenshots.map((src, i) => (
                                                <div key={i}>
                                                    <img
                                                        src={src}
                                                        alt={`${game.title} screenshot ${i + 1}`}
                                                        className="screenshotImage"
                                                    />
                                                </div>
                                            ))}
                                        </Slider>
                                    )
                                ) : (
                                    <img
                                        src={screenshots[0]}
                                        alt={`${game.title} screenshot`}
                                        className="screenshotImage"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}

            <div className="contactSection">
                <p className="contactLabel">Get in touch</p>
                <a href="mailto:nick.gerwen123@gmail.com" className="contactEmail">nick.gerwen123@gmail.com</a>
            </div>
        </div>
    );
};

export default Home;
